import { Alert, Button } from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Application, RegistryDocument } from '../../data/types';
import DocumentViewerDialog from '../../documents/DocumentViewerDialog';
import { date, money, units as formatUnits } from '../../lib/format';
import { useRegistry } from '../../store/RegistryStore';
import { KeyValueGrid, SectionCard, StatusChip } from '../../ui/primitives';

export default function ApplicationIssuePanel({ application }: { application: Application }) {
  const { state } = useRegistry();
  const [viewing, setViewing] = useState<RegistryDocument | null>(null);
  const run = state.issueRuns.find((item) => item.id === application.issueRunId);
  if (!run) return null;
  const line = run.lines.find((item) => item.applicationId === application.id);
  const certificate = state.documents.find(
    (doc) =>
      doc.refId === run.id && doc.kind === 'UNIT_CERTIFICATE' && doc.holderId === line?.holderId,
  );
  return (
    <SectionCard
      title="Units"
      subtitle={`Financial close ${run.reference}`}
      action={<StatusChip status={run.status} />}
    >
      {run.status === 'PENDING_APPROVAL' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Included in a unit issue waiting for approval.
        </Alert>
      )}
      <KeyValueGrid
        columns={3}
        items={[
          { label: 'Settlement date', value: date(run.settlementDate) },
          { label: 'Investment amount', value: money(line?.amountPaid ?? 0, true) },
          { label: 'Paid per unit', value: `$${run.paidPerUnit.toFixed(4)}` },
          { label: 'Units', value: formatUnits(line?.units ?? 0) },
          { label: 'Certificate', value: line?.certificateNo ?? 'Numbered on issue' },
          {
            label: 'Certificate released',
            value: run.certificatesReleasedAt ? date(run.certificatesReleasedAt) : 'Not yet',
          },
        ]}
      />
      {certificate && (
        <Button size="small" sx={{ mt: 2 }} onClick={() => setViewing(certificate)}>
          View certificate
        </Button>
      )}
      <Button
        size="small"
        sx={{ mt: 2 }}
        component={RouterLink}
        to={`/manager/offers/${run.offerId}/close`}
      >
        Open financial close
      </Button>
      <DocumentViewerDialog doc={viewing} onClose={() => setViewing(null)} />
    </SectionCard>
  );
}
